import {
  CreateSubscriptionData,
  Subscription,
} from '@models/membership/Subscription'
import { subscriptionSchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createSubscription as createSubscriptionAPI,
  deleteSubscription as deleteSubscriptionAPI,
  fetchSubscriptionById,
  fetchSubscriptions,
  updateTagsForSubscription as updateTagsForSubscriptionAPI,
} from '@services/membership/SubscriptionService'
import { AppDispatch, RootState } from '@store/store'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

const initialState: SBState<Subscription> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'subscription',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getSubscriptionsSuccess,
  setQuery: setSubscriptionQuery,
  reset: resetSubscriptionState,
  resetQueryAndIds: resetSubscriptionQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: Subscription }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectSubscriptions = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedSubscription = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectSubscriptionById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectSubscriptionsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedSubscription =
  (subscription: Subscription | number) => async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(getIdOrModelId<Subscription>(subscription))
    )

export const getSubscriptions = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchSubscriptions(params),
    [subscriptionSchema],
    slice.actions
  )

export const getSubscriptionWithId = (id: number) =>
  SBAPI.FetchDispatched<Subscription>(
    fetchSubscriptionById(id),
    subscriptionSchema,
    slice.actions
  )

export const createSubscription = (subscription: CreateSubscriptionData) =>
  SBAPI.CreateDispatched(
    createSubscriptionAPI(subscription),
    subscriptionSchema,
    slice.actions
  )

export const deleteSubscription = (subscription: Subscription) =>
  SBAPI.DestroyDispatched(
    deleteSubscriptionAPI(subscription.id),
    subscription,
    slice.actions
  )

export const updateSubscriptionTags = (subscription: Subscription) =>
  SBAPI.UpdateDispatched<Subscription>(
    updateTagsForSubscriptionAPI(subscription.id, subscription.tags ?? []),
    subscription,
    slice.actions
  )
